<template>
	<div>
		<v-alert type="error" v-if="error_msg.length !== 0">
			{{ error_msg }}
		</v-alert>
		<ProgressLinear v-if="loading" />
		<template v-if="!loading">
			<div class="task-list-item my-5" v-for="item in tasklist" :key="item.id">
				<div class="task-title">
					<TaskTitle 
						v-if="item.id !== 0"
						:isOpen="item.switch" 
						:name="item.name" 
						:isDynamic="item.is_dynamic" 
						:owner="typeof(item.owner) === 'string' ? item.owner : item.owner.name"
						:isEdit="edit_id === item.id"
						:color="color"
						@rename="onRename($event, item)"
						@click="onTasklistClick(item)"
					/>
					<v-btn icon @click="onRefresh(item)">
						<RefreshIcon width="20" height="20" :color="'rgba(25, 42, 62, 0.7)'"/>
					</v-btn>
					<TaskMenu
						v-if="item.id !== 0"
						:key="item.id"
						:isOwned="item.is_owned"
						:isPinned="item.is_pinned"
						@click-menu-item="onClickMenuItem($event, item)"
						@delete="onDeleteMenuItem(item)"
					/>	
					<div v-if="item.id === 0">Unsaved Tasklist</div>
					<div v-if="current_unsaved_ids.includes(item.id)" class="save-as-bnt mb-1" :style="$vuetify.breakpoint.smAndDown ?'width: 100%':''"  @click="onSaveChange(item)">
						<div v-if="item.id === 0" class="task-title">
							<SaveIcon class="mr-1" width="20" height="20" :color="'#FFFFFF'"/>
							<div>Save as list</div>
						</div>
						<div v-if="item.id !== 0 && !item.is_owned" class="task-title">
							<SaveIcon class="mr-1" width="20" height="20" :color="'#FFFFFF'"/>
							<div>{{ $vuetify.breakpoint.smAndDown ? 'Save as':'Save as your own list' }}</div>
						</div>
					</div>
					<div v-if="item.id === 0" class="delete-bnt mb-1" :style="$vuetify.breakpoint.smAndDown ?'width: 100%':''" @click="onDeleteChange(item)">
						<div class="task-title">
							<CloseIcon class="mr-1"  width="20" height="20" :color="'#F45252'"/>
							<div>Remove list</div>
						</div>
					</div>	
				</div>
				<ProgressLinear v-if="loading_key === item.id && item.switch" />
				<TaskBulk 
					class="my-2"
					style="width: 100%"
					v-if="item.switch && loading_key !== item.id && item.bulk_visible && !$vuetify.breakpoint.smAndDown" 
					:color="color"
					:statuses="statuses"
					:isPrivilege="privileges.includes(privilege_tr)"
					:bulk_assigned="item.bulk_assigned"
					:bulk_status="item.bulk_status"
					:bulk_kind="item.bulk_kinds"
					@status-change="onBulkStatusChange($event, item)"
					@assigned-change="onBulkAssignedChange($event, item)"
				/>
				<TaskTable 
					v-if="item.switch && loading_key !== item.id && !$vuetify.breakpoint.smAndDown" 
					:key="item.id"
					:headers="headers" 
					:items="filterTasks(item.task, item.bulk_tasks ? item.bulk_tasks : [])"
					:color="color"
					:statuses="statuses"
					:perpage="perpage"
					:sort="item.sort_init"
					:search="item.search_init"
					@perpage="onPerpageChange($event)"
					@log-time="task_id = $event; tasklist_id = item.id;"
					@stop_recording="onStopRecording()"
					@complete="onCompleteClick($event, item.id)"
					@status-change="onStatusChange($event, item.id)"
					@row-click="onTaskClick($event, item.id)"
					@selected="onSelected($event, item)"
					@error="error_msg = $event"
					@search="onSearchChange($event, item)"
					@sort="onSortChange($event, item)"
					@find-ts="find_timesheet_id = $event"
				/>
				<template v-if="item.switch && loading_key !== item.id && $vuetify.breakpoint.smAndDown">
					<CalendarItem
						class="mt-1"
						v-for="task in item.task"
						:key="task.id" 
						:task="task"
						:statuses="statuses"
						:isPrivilege="privileges.includes(privilege_tr)"
						:jobValid="task.job_valid"
						@edit="onTaskClick($event, item.id)"
						@share="onShareMenuItem($event)"
						@delete="onDeleteTaskMenuItem($event, item.id)"
						@duplicate="onDuplicateMenuItem($event, item.id)"
						@status="onStatusChange($event, item.id)"
						@log-time="task_id = $event; tasklist_id = item.id;"
					/>
					<div class="mx-6" v-if="item.task.length === 0">No items in list</div>
				</template>
			</div>
		</template>
		<v-dialog
			v-if="task_detail_visible"
			v-model="task_detail_visible"
			persistent
			:max-width="$vuetify.breakpoint.width <= 1024 ? maxWidth_xs : width"
		>
			<TaskDetail
				:color="color"
				:task="task_detail_item"
				:isDuplication="isDuplication"
				:isPrivilege="privileges.includes(privilege_tr)"
				@find-ts="find_timesheet_id = $event"
				@task-close="onCloseTaskDetail()"
			/>
		</v-dialog>
		<ConfirmDialog 
			v-if="complete_visible"
			:visible="complete_visible" 
			:type="`info`" 
			:title_text="`Complete Task?`" 
			:content_text="`Complete a Task if all work and timesheets have been done.`"
			:confirm_button="`Yes, Complete Task`"
			:icon_name="`mdi-check`"
			:maxWidth="500"
			@cancel="onCompleteCancel()"
			@ok="onComplete()"
		/>
		<DeleteConfirmDialog 
			v-if="delete_visible"
			:visible="delete_visible" 
			:color="'error'"
			:title="`You are about to delete Tasklist ‘${delete_item.name}’`" 
			:content="'This action cannot be undone. Are you sure?'"
			:ok="'Yes, delete'"
			:icon_name_title="'mdi-alert-circle'"
			:icon_name_front="'mdi-delete-outline'"
			:maxWidth="500"
			@cancel="onDeleteCancel()"
			@ok="onDelete()"
		/>
		<DeleteConfirmDialog 
			v-if="delete_task_visible"
			:visible="delete_task_visible" 
			:color="'error'"
			:title="`You are about to delete this Task '${delete_task.title}'.`" 
			:content="`This action cannot be undone. Are you sure?`"
			:ok="'Yes, delete'"
			:icon_name_title="'mdi-alert-circle'"
			:icon_name_front="'mdi-delete-outline'"
			:maxWidth="500"
			@cancel="onDeleteCancel()"
			@ok="onDeleteTask()"
		/>
		<DialogNotice
			v-if="visible_notice" 
			:visible="visible_notice" 
			:text="notice_text"
		/>
		<CalendarShareDialog 
			v-if="share_task_visible"
			:visible="share_task_visible"
			:taskId="share_task_id"
			@copy-link="onCopyLink($event)"
			@close="share_task_visible = false"
		/>
		<TaskSaveAsDialog 
			v-if="save_as_visible" 
			:visible="save_as_visible" 
			:title_text="'Save tasklist as...'"
			:content_text="'New Tasklist name'"
			:confirm_button="'Save Tasklist'"
			:icon="save_icon"
			:color="color"
			:isDynamic="save_as_item && save_as_item.id === 0"
			@cancel="save_as_visible = false"
			@ok="onSaveAs($event)"
		/>
		<TaskShareDialog 
			v-if="share_visible" 
			:visible="share_visible" 
			:tasklistId="share_item.id"
			:tasklistAcl="share_item.acl ? share_item.acl : []"
			:tasklistOwner="share_item.owner"
			:color="color"
			@close="share_visible = false"
			@copy-link="onCopyLink($event)"
		/>
		<ListTimesheetDialog
			v-if="find_timesheet_id !== 0"
			:visible="find_timesheet_id !== 0"
			:maxWidth="$vuetify.breakpoint.width * 0.85"
			:taskId="find_timesheet_id"
			@close="find_timesheet_id = 0"
			@openTs="timesheet_detail_item = $event; timesheet_detail_visible = true"
		/>
		<v-dialog
			v-if="timesheet_detail_visible"
			v-model="timesheet_detail_visible "
			persistent
			:max-width="$vuetify.breakpoint.width <= 1024 ? maxWidth_xs : width"
		>
			<EditTimesheet
				:editItem="timesheet_detail_item"
				:isAddTimesheet="false"
				:isLogTimesheet="true"
				@cancel-timesheet="timesheet_detail_item = null; timesheet_detail_visible = false"
			/>
		</v-dialog>
		<LogTimesheet v-if="task_id !== 0" :visible="task_id !== 0" :taskId="task_id" @close="onCloseTimesheet()"/>
		<LogTimesheet
			v-if="auto_time !== 0"
			:visible="auto_time !== 0"
			:taskId="recording_task_id"
			:autoDuration="auto_time"
			@close="auto_time = 0"
		/>
		<ProgressCircle
			v-if="updating"
			:visible="updating"
			:color="color"
		/>
	</div>
</template>
<script>
import TaskTitle from "@/views/tasks/task_title.vue"
import TaskMenu from "@/views/tasks/task_menu.vue";
import TaskBulk from "@/views/tasks/task_bulk.vue";
import TaskTable from "@/views/tasks/task_table.vue";
import TaskDetail from "@/views/tasks/edit_task.vue";
import ConfirmDialog from "@/components/dialogs/confirm_dialog.vue";
import DeleteConfirmDialog from "@/components/dialogs/delete_comfirm_dialog.vue";
import TaskSaveAsDialog from "@/views/tasks/task_save_as_dailog.vue";
import TaskShareDialog from "@/views/tasks/task_share_dialog.vue";
import ProgressLinear from "@/components/progress/progress_getdata_linear.vue";
import ProgressCircle from "@/components/dialogs/progress_dialog.vue";
import DialogNotice from "@/components/dialogs/notice_dialog.vue";
import SaveIcon from "@/components/icons/save.vue";
import CloseIcon from "@/components/icons/close.vue";
import RefreshIcon from "@/components/icons/refresh.vue";
import LogTimesheet from "@/components/tasks/task_timesheet";
import CalendarItem from "@/views/timesheets/calendar_item.vue";
import CalendarShareDialog from "@/views/timesheets/calendar_share_dialog.vue";
import ListTimesheetDialog from "@/views/timesheets/list_timesheet_dialog.vue";
import EditTimesheet from "@/views/timesheets/new_timesheet.vue";

import config from "@/config.js";
import moment from 'moment';

const TASKLIST_CLASS = 'restful.tasks.lists';

const SAVE_ICON = {
	name: SaveIcon,
	color: "white",
	width: 24, 
	height: 24,
};

const JOr = 'JOr';
/* the user will be able to create and amend tasks */
const TR = 'TR';


const Date_Options_Suggested = 'suggested';
const Date_Options_Actual = 'actual';
const Date_Options_No = 'nodates';

const ALLEMPLOYEES = 2;
const ROLES = 1;
const DEPARTMENTS = 0;

export default {
	name: 'TaskList',
	props: {
		color: {
			type: String,
			default: '',
		},
		statuses: {
			type: Array,
			default: () => [],
		},
		headers: {
			type: Array,
			default: () => [],
		},
		statusesFiltered: {
			type: Array,
			default: () => [],
		},
		search_param: {
			type: Object,
			default: () => {}
		},
		isSaveAll: {
			type: Boolean,
			default: false
		},
		perpage: {
			type: Number,
			default: 10
		}
	},
	data() {
		this.maxWidth_xs = '100%';
		this.width = '800';
		this.privilege_tr = TR;

		return {
			error_msg: '',
			loading: false,
			loading_key: -1,
			save_icon: SAVE_ICON,
			pin_id: '',
			updating: false,

			tasklist: [],
			complete_task_id: 0,
			complete_tasklist_id: 0,
			job_invalid_status_ids: [],
			
			complete_visible: false,
			delete_visible: false,
			save_as_visible: false,
			share_visible: false,

			share_task_id: 0,
			share_task_visible: false,

			delete_task: null,
			delete_task_visible: false,

			isDuplication: false,

			visible_notice: false,
			notice_text: [],
			tid: null,
		
			current_unsaved_ids: [],
			edit_id: -1,
			share_item: { id: 0, acl: null, owner: { id: 0 } },
			save_as_item: null,
			delete_item: null,

			task_detail_item: null,
			task_detail_visible: false,

			find_timesheet_id: 0,
			timesheet_detail_visible: false,
			timesheet_detail_item: null,

			bulk_task_ids: [],
			bulk_task_ids_remove: [],

			task_id: 0,
			tasklist_id: 0,
			auto_time: 0,
		}
	},
	components: {
		TaskTitle,
		TaskMenu,
		TaskBulk,
		TaskTable,
		TaskDetail,
		ConfirmDialog,
		DeleteConfirmDialog,
		TaskSaveAsDialog,
		TaskShareDialog,
		ProgressLinear,
		ProgressCircle,
		DialogNotice,
		LogTimesheet,
		SaveIcon,
		CloseIcon,
		RefreshIcon,
		CalendarItem,
		CalendarShareDialog,
		ListTimesheetDialog,
		EditTimesheet
	},
	beforeDestroy() {
		if(this.tid) {
			clearTimeout(this.tid);
		}
	},
	async created() {
		if (this.$route.params.id && this.$route.params.id !== '0') {
			this.pin_id = this.$route.params.id;
		}
		await this.getJobStatusIds();

		this.getTasklists();
	},
	computed: {
		recording_task_id() {
			let recording_task_id_tmp = 0;
			if (this.$store.state.recording && this.$store.state.recording.task_id !== 0) {
				recording_task_id_tmp = this.$store.state.recording.task_id;
			}
			return recording_task_id_tmp;
		},
		completedJobPrivilege() {
			let isCompletedJobTmp = false;
			if (this.$store.state.privileges && this.$store.state.privileges.length !== 0 && this.$store.state.privileges.includes(JOr)) {
				isCompletedJobTmp = true;
			}
			return isCompletedJobTmp
		},
		privileges () {
			return this.$store.state.privileges;
		},
	},
	watch: {
		search_param() {
			if (this.search_param) {
				this._loadTasks(this.search_param.id, this.search_param.settings, true);
			}
		},
		current_unsaved_ids() {
			this.$emit('current_unsaved', this.current_unsaved_ids);

			if (this.current_unsaved_ids.length !== 0 && this.isSaveAll) {
				const unsaved_tasklist = this.tasklist.filter(t => { return t.id ===  this.current_unsaved_ids[0]});
				this.onSaveChange(unsaved_tasklist[0]);
			}
		},
		isSaveAll() {
			if (this.isSaveAll) {
				const unsaved_tasklist = this.tasklist.filter(t => { return t.id ===  this.current_unsaved_ids[0]});
				this.onSaveChange(unsaved_tasklist[0]);
			}
		},
		task_detail_item() {
			const isDetail = this.task_detail_item ? true : false;
			this.$emit('isDetail', isDetail);
		}
	},
	methods: {
		async getJobStatusIds() {
			const url = '/api/jobs/meta';
			await axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then((val) => {
					const { status, data, reason } = val.data;

					if (status == "OK") {
						const statuses_arr = data.substatusses;
						const job_invalid_statusses = statuses_arr.filter(s => { return !config.job_status_group_nomal.includes(s.group) });
						this.job_invalid_status_ids = job_invalid_statusses.map(s => { return s.id });
					} else {
						this.error_msg = reason;
					}
				})
				.catch((e) => {
					this.error_msg = e.message;
				});
		},
		getTasklists(open_task=null) {
			this.loading = true;

			const params = [];
			params.push("class=" + encodeURIComponent(TASKLIST_CLASS));
			params.push("include_shared=true");
			params.push("include_hidden=true");

			let url = "/api/worklists";
			if (params.length > 0) {
				url+= "?" + params.join('&');
			}

			axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then(val => {
					const { status, data, reason } = val.data;

					if (status == "OK") {
						const pin_id = this.pin_id.length !== 0 ? Number(this.pin_id) : 0;
						const arr_tmp = [];
						data.forEach(d => {
							const obj = d;
							obj['switch'] = false;
							obj['task'] = [];
							arr_tmp.push(obj);
						});

						arr_tmp.sort((a, b) => { return a.id - b.id });

						this.tasklist = arr_tmp;

						const open_tasklist = this.tasklist.filter(t => {
							return t.id === pin_id;
						});
						if (open_tasklist.length !== 0) {
							this.onTasklistClick(open_tasklist[0]);
						}
						if (open_task) {
							this.onTasklistClick(open_task, true);
						}

					} else {
						this.error_msg = reason;
					}
				})
				.catch(e => {
					this.error_msg = e.message;
				}).finally(() => {
					this.loading = false;
				});
		},
		filterTasks(tasks, bulk_tasks) {
			const filterRes = [];

			tasks.forEach(task => {
				if (!this.statusesFiltered.includes(task.status.id)) return;

				const obj = {};	

				this.headers.forEach(header => {
					if (!header.showable) return;
					
					if (header.value === 'select') {
						if (bulk_tasks.includes(task.id)) {
							obj[header.value] = true;
						} else {
							obj[header.value] = false;
						}
					} else if (header.value === 'log') {
						obj[header.value] = { log: true, status_type: task.status.type };
					} else if (header.value === 'auto') {
						obj[header.value] = { id: task.id, status_type: task.status.type };
					} else if (header.value === 'display') {
						obj[header.value] = `${task.job.code}-${task.job.name}`;
					} else if (header.value === 'days') {
						obj[header.value] = task.task_days;
					} else if (header.value === 'start_date') {
						obj[header.value] = task.start_date ? moment(task.start_date, ["DO MMM YYYY", "DD MMM YYYY"]).format() : '';
					} else if (header.value === 'end_date') {
						obj[header.value] = task.end_date ? moment(task.end_date, ["DO MMM YYYY", "DD MMM YYYY"]).format() : '';
					} else if (header.value === 'isComplete') {
						obj[header.value] = task.status.type === config.status_completed_type ? true : false;
					} else if (header.value === 'budget') {
						obj[header.value] = this._minutesToBudget(task.budget);
					} else if (header.value === 'actual') {
						obj[header.value] = this._minutesToBudget(task.actual);
					} else if (header.value === 'remaining') {
						obj[header.value] = this._minutesToBudget(task.remaining_minutes);
					} else if (header.value === 'priority') {
						obj[header.value] = task.priority + 1;
					}  else if (header.value === 'phase') {
						obj[header.value] = task.phase.name;
					} else {
						obj[header.value] = task[header.value];
					}
				});			

				obj['id']	= task.id;
				obj['isSuggested'] = task.is_suggested;
				obj['job_valid'] = task.job_valid;
				obj['other_edit'] = this.privileges.includes(this.privilege_tr);

				filterRes.push(obj);
			});
			
			return filterRes;
		},
		onTasklistClick(item, isManual=false) {
			if (isManual) {
				item.switch = true;

				this.tasklist.forEach(task => { 
					if (task.id === item.id) {
						task.switch = true;
					}
				});
			} else {
				item.switch = !item.switch;
			}
			
			if (item.switch) {
				this._loadTasks(item.id, item.settings);
			} else {
				const tasklist_tmp = JSON.parse(JSON.stringify(this.tasklist));
				this.tasklist = tasklist_tmp;
			}
		},
		onRefresh(item) {
			this._loadTasks(item.id, item.settings);
		},
		onTaskClick(task, tasklist_id) {
			this.task_detail_item = task;
			this.tasklist_id = tasklist_id;
			this.task_detail_visible = true;
		},
		onSelected(event, item) {
			const task_filter = item.task.filter(t => { return event.includes(t.id) });

			const bulk_visible = task_filter.length !== 0;

			const bulk_status = task_filter.map(t => { return t.status});
			const map_statuses = new Map();
			bulk_status.forEach(s => {
				if (!map_statuses.has(s.id)) {
					map_statuses.set(s.id, s);
				}
			});
			const bulk_status_filter = [...map_statuses.values()];

			let bulk_assigned = [];	
			task_filter.forEach(t => {
				const arr = [...t.task_for];
				bulk_assigned = bulk_assigned.concat(arr);
			});

			const bulk_kind = task_filter.map(t => { return t.kind });
			const map_kinds = new Map();
			bulk_kind.forEach(s => {
				if (s && !map_kinds.has(s.id)) {
					map_kinds.set(s.id, s);
				}
			});
			const bulk_kind_filter = [...map_kinds.values()];

			
			const tasklist_tmp = [];
			this.tasklist.forEach(t => {
				if (t.id === item.id) {
					t['bulk_tasks'] = event;
					t['bulk_visible'] = bulk_visible;
					t['bulk_status'] = bulk_status_filter;
					t['bulk_assigned'] = bulk_assigned;
					t['bulk_kinds'] = bulk_kind_filter;
					tasklist_tmp.push(t);
				} else {
					tasklist_tmp.push(t);
				}
			});
			
			this.tasklist = tasklist_tmp;
		},
		async onBulkStatusChange(dest_status, item) {
			this.bulk_task_ids = item.bulk_tasks;
			const bulks = item.bulk_tasks;

			for (let index = 0; index < bulks.length; index++) {
				const task_id = bulks[index];
				if (this.bulk_task_ids.includes(task_id)) {
					await this._updateTaskStatus(task_id, dest_status.id, item.id, bulks);
				}
			}

			if (this.bulk_task_ids.length === 0) {
				this.getTasklists(item);

				this.notice_text.push('Status has been changed successfully');
				this.visible_notice = true;
				this.tid = setTimeout(this._close, 2000);
			}
		},
		async onBulkAssignedChange(dest_assigned, item) {
			const dest_assigned_keys = dest_assigned.map(a => {
				const id = a.mode === DEPARTMENTS ? a.department_id : (a.mode === ALLEMPLOYEES ? a.abook_id : a.role_id);
				return `${a.mode}_${id}`
			});
			
			const bulks = item.bulk_tasks;
			for (let index = 0; index < bulks.length; index++) {
				const task_id = bulks[index];
				const current_task = (item.task.filter(t => { return t.id === task_id }))[0];
				
				/** check which one needs to add or remove */
				const exist_assigned_keys = current_task.task_for.map(a => { 
					const id = a.Type === DEPARTMENTS ? a.DepartmentId : (a.Type === ALLEMPLOYEES ? a.AbookId : a.RoleId);
					return `${a.Type}_${id}`
				});

				const add_dest_assigned_tmp = dest_assigned_keys.filter(d => { return !exist_assigned_keys.includes(d)});
				if (add_dest_assigned_tmp.length !== 0) {
					const map_res_a = new Map();
					add_dest_assigned_tmp.forEach(s => {
						if (!map_res_a.has(s)) {
							map_res_a.set(s, s);
						}
					});
					const add_dest_unique = [...map_res_a.values()];

					/** build request params */
					const add_dest_assigned = [];
					add_dest_unique.forEach(a => {
						const assigned_id_split = a.split('_');
						const assigned_obj = {};
						assigned_obj['mode'] = Number(assigned_id_split[0]);

						if (Number(assigned_id_split[0]) === DEPARTMENTS) {
							assigned_obj['department_id'] = Number(assigned_id_split[1]);
						}
						if (Number(assigned_id_split[0]) === ROLES) {
							assigned_obj['role_id'] = Number(assigned_id_split[1]);
						}
						if (Number(assigned_id_split[0]) === ALLEMPLOYEES) {
							assigned_obj['abook_id'] = Number(assigned_id_split[1]);
						}

						add_dest_assigned.push(assigned_obj);
					});

					this.updating = true;
					this.bulk_task_ids.push(task_id);
					await this._updateTaskAssigned(task_id, add_dest_assigned, true);
				}
				
				const remove_dest_assigned_tmp = exist_assigned_keys.filter(e => { return !dest_assigned_keys.includes(e)});
				if (remove_dest_assigned_tmp.length !== 0) {
					const map_res_b = new Map();
					remove_dest_assigned_tmp.forEach(s => {
						if (!map_res_b.has(s)) {
							map_res_b.set(s, s);
						}
					});
					const remove_dest_unique = [...map_res_b.values()];

					/** build request params */
					const remove_dest_assigned = [];
					remove_dest_unique.forEach(a => {
						const assigned_id_split = a.split('_');
						const assigned_obj = {};
						assigned_obj['mode'] = Number(assigned_id_split[0]);

						if (Number(assigned_id_split[0]) === DEPARTMENTS) {
							assigned_obj['department_id'] = Number(assigned_id_split[1]);
						}
						if (Number(assigned_id_split[0]) === ROLES) {
							assigned_obj['role_id'] = Number(assigned_id_split[1]);
						}
						if (Number(assigned_id_split[0]) === ALLEMPLOYEES) {
							assigned_obj['abook_id'] = Number(assigned_id_split[1]);
						}

						remove_dest_assigned.push(assigned_obj);						
					});

					this.updating = true;
					this.bulk_task_ids_remove.push(task_id);
					await this._updateTaskAssigned(task_id, remove_dest_assigned, false);
				}
			}

			if (this.bulk_task_ids.length === 0 && this.bulk_task_ids_remove.length === 0) {
				this.getTasklists(item);

				this.updating = false;
				this.notice_text.push('Assigned has been changed successfully');
				this.visible_notice = true;
				this.tid = setTimeout(this._close, 2000);
			}
		},
		onClickMenuItem(param, item) {
			const key = typeof(param) === 'string' ? param : param.key;

			if (key === 'search') {
				if (!item.switch) {
					item.switch = true;
					this._loadTasks(item.id, item.settings);
				}
				const item_obj = {
					id: item.id,
					name: item.name,
					settings: item.settings
				};
				this.$emit('search', item_obj);
			}
			if (key === 'share') {
				if (item.is_owned) {
					this._loadTasklist(item.id);
					this.share_visible = true;
				} 
			}
			if (key === 'pin') {
				this._pinTasklist(item, param.enabled);
			}
			if (key === 'rename') {
				if (item.is_owned) {
					this.edit_id = item.id;
				}
			}
			if (key === 'save') {
				this.save_as_item = item;
				this.save_as_visible = true;
			}
		},
		onDeleteMenuItem(item) {
			this.delete_item = item;
			this.delete_visible = true;
		},
		onCompleteClick(task_id, tasklist_id) {
			this.complete_visible = true;
			this.complete_task_id = task_id;
			this.complete_tasklist_id = tasklist_id;
		},
		onCompleteCancel() {
			this.complete_visible = false;
			this.complete_task_id = 0;
			this.complete_tasklist_id = 0;
		},
		onComplete() {
			this.complete_visible = false;

			const complete_status = this.statuses.filter(status => { return status.type === config.status_completed_type });
			this._updateTaskStatus(this.complete_task_id, complete_status[0].id, this.complete_tasklist_id);
		},
		onRename(name, item) {
			const param = {
				id: item.id,
				class: item.class,
				name: name,
				settings: typeof(item.settings) === 'string' ? item.settings :JSON.stringify(item.settings),
				is_dynamic: item.is_dynamic,
				details: item.details,
			};

			this._updateTasklist(item.id, param);
		},
		onSearchChange(search_obj, item) {
			const settings = typeof(item.settings) === 'string' ? JSON.parse(item.settings) : item.settings;

			if (search_obj) {
				settings['search'] = search_obj;
			} else {
				settings['search'] = null;
			}
			
			const param = {
				id: item.id,
				class: item.class,
				name: item.name,
				settings: JSON.stringify(settings),
				is_dynamic: item.is_dynamic,
				details: item.details,
			};

			this._updateTasklist(item.id, param, true);
		},
		onSortChange(sort_obj, item) {
			const settings = typeof(item.settings) === 'string' ? JSON.parse(item.settings) : item.settings;
			if (sort_obj) {
				settings['sort'] = sort_obj;
			} else {
				settings['sort'] = null;
			}

			const param = {
				id: item.id,
				class: item.class,
				name: item.name,
				settings: JSON.stringify(settings),
				is_dynamic: item.is_dynamic,
				details: item.details,
			};

			this._updateTasklist(item.id, param, true);
		},
		onSaveAs(params) {
			const param = {
				id: 0,
				class: this.save_as_item.class,
				name: typeof(params) === 'string' ? params : params.name,
				settings: typeof(this.save_as_item.settings) === 'string' ? this.save_as_item.settings :JSON.stringify(this.save_as_item.settings),
				is_dynamic: typeof(params) === 'string' ? this.save_as_item.is_dynamic : params.dynamic,
				details: this.save_as_item.details ? this.save_as_item.details : null,
			};
			this._createTasklist(param);
		},
		onCopyLink(isSuccessful) {
			this.share_task_visible = false;

			const text = isSuccessful ? 'Link copied successfully.':'Link copied failed.';
			this.notice_text.push(text);
			this.visible_notice = true;
			this.tid = setTimeout(this._close, 2000);
		},
		onDeleteCancel() {
			this.delete_visible = false;
			this.delete_item = null;

			this.delete_task_visible = false;
			this.delete_task = null;
		},
		onDelete() {
			this.delete_visible = false;
			this._deleteTasklist(this.delete_item.id, this.delete_item.name);
		},
		onPerpageChange(perpage) {
			this.$emit('perpage', perpage);
		},
		onStatusChange(param, tasklist_id) {
			this._updateTaskStatus(param.task_id, param.status_id, tasklist_id);
		},
		onSaveChange(item) {
			if (!item.is_owned || (typeof(item.id) === 'string' && item.id.startsWith('0')) ) {
				this.save_as_item = item;
				this.save_as_visible = true;
			} else {
				const param = {
					id: item.id,
					class: item.class,
					name: item.name,
					settings: typeof(item.settings) === 'string' ? item.settings :JSON.stringify(item.settings),
					is_dynamic: item.is_dynamic,
					details: item.details ? item.details: null,
				};
				this._updateTasklist(item.id, param);
			}
		},
		onDeleteChange(item) {
			this.tasklist = this.tasklist.filter(t => { return t.id !== item.id });
			this.current_unsaved_ids = this.current_unsaved_ids.filter(c => { return c !== item.id });
		},
		onCloseTaskDetail() {
			const item = this.tasklist.filter(t => { return t.id === this.tasklist_id });
			this.getTasklists(item[0]);
			this.task_detail_item = null;
			this.task_detail_visible = false;
			this.isDuplication = false;
		},
		onCloseTimesheet() {
			const item = this.tasklist.filter(t => { return t.id === this.tasklist_id });
			this.getTasklists(item[0]);

			this.task_id = 0;
			this.tasklist_id = 0;
		},
		onStopRecording() {
			const new_time = new Date().getTime();
			const time_passed_in_ms = new_time - this.$store.state.recording.started;
			const time_passed_in_mins = Math.round(time_passed_in_ms / 60000.0);
			const total_time = time_passed_in_mins;

			if (total_time === 0) {
				this.$store.dispatch('stop_recording');
			} else {
				this.auto_time = total_time;
			}
		},
		onShareMenuItem(task_id) {
			this.share_task_id = task_id;
			this.share_task_visible = true;
		},
		onDeleteTaskMenuItem(task, worklistId) {
			this.delete_task = {
				id: task.id,
				title: task.title,
				worklistId: worklistId
			}
			
			this.delete_task_visible = true;
		},
		onDeleteTask() {
			this.delete_task_visible = false;
			this._deleteTask(this.delete_task.id, this.delete_task.title, this.delete_task.worklistId);
		},
		onDuplicateMenuItem(task, tasklist_id) {
			this.isDuplication = true;
			this.onTaskClick(task, tasklist_id);
		},
		async _updateTaskStatus(task_id, status_id, tasklist_id, bulk=[]) {
			const params = [];
			params.push('status_id=' + status_id);

			let url = `/api/tasks/${task_id}/set_status`;
			if (params.length > 0) {
				url+= "?" + params.join('&');
			}
			
			await axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then((val) => {
					let json = val.data;

					// For some reason axios isn't always converting our data
					if ((typeof json) == "string") {
						json = JSON.parse(json);
					}

					const { status, data, reason } = json;

					if (status == "OK") {
						if (bulk.length !== 0) {
							this.bulk_task_ids = this.bulk_task_ids.filter(b => { return b !== task_id });
							
						} else {
							const tasklist_tmp = [];

							this.tasklist.forEach(item => {
								if (item.id === tasklist_id) {
									const task_tmp = [];
									item.task.forEach(value => {
										if (value.id === task_id) {
											value['status'] = data.status;
											task_tmp.push(value);
										} else {
											task_tmp.push(value);
										}
									});

									item.task = task_tmp;
									tasklist_tmp.push(item);
								} else {
									tasklist_tmp.push(item);
								}
							});

							this.tasklist = tasklist_tmp;

							if (this.complete_task_id !== 0) {
								this.complete_task_id = 0;
							}
							if (this.complete_tasklist_id !== 0) {
								this.complete_tasklist_id = 0;
							}

							this.notice_text.push('Status has been changed successfully');
							this.visible_notice = true;
							this.tid = setTimeout(this._close, 2000);
						}
					} else {
						this.error_msg = reason;
					}
				})
				.catch((e) => {
					this.error_msg = e.message;
				})
		},
		async _updateTaskAssigned(task_id, assigned, is_add=true) {
			const is_for = [];
			assigned.forEach(a => {
				let department_id = 0;
				let role_id = 0;
				let employee_id = 0;

				if (a.mode === 0) {
					department_id = a.department_id;
				}
				if (a.mode === 1) {
					role_id = a.role_id;
				}
				if (a.mode === 2) {
					employee_id = a.abook_id;
				}

				const obj = {
					mode: a.mode,
					role_id: role_id,
					department_id: department_id,
					employee_id: employee_id
				};
				is_for.push(obj)
			});
			const params_obj = {
				is_for: is_for
			}


			const url_suffix = is_add ? 'set_taskfor' : 'remove_taskfor';
			const url = `/api/tasks/${task_id}/${url_suffix}`;
			
			await axios
				.post(url, params_obj, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then((val) => {
					let json = val.data;

					const { status, reason } = json;

					if (status == "OK") {
						if (is_add) {
							this.bulk_task_ids = this.bulk_task_ids.filter(b => { return b !== task_id });
						} else {
							this.bulk_task_ids_remove = this.bulk_task_ids_remove.filter(b => { return b !== task_id });
						}
					} else {
						this.error_msg = reason;
					}
				})
				.catch((e) => {
					this.error_msg = e.message;
				});
		},
		_loadTasks(worklist_id, settings, isSearch=false) {
			if (worklist_id === 0) {
				this.loading = true;
			} else {
				this.loading_key = worklist_id;
			}
			const params = this._setParams(settings);
			const filter_params = this._setFilterParams(settings);
			const selected_filter_params = this._getSelectedFiltersParams(settings);

			let url = "/api/tasks";
			if (params.length > 0) {
				url+= "?" + params.join('&');
			}

			axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then((val) => {
					let json = val.data;

					// For some reason axios isn't always converting our data
					if ((typeof json) == "string") {
						json = JSON.parse(json);
					}

					const { status, data, reason } = json;

					if (status == "OK") {
						let data_filter = []
						if (filter_params.length !== 0) {
							data_filter = this._filterDate(data, filter_params);

							const map_res = new Map();
							data_filter.forEach(s => {
								if (!map_res.has(s.id)) {
									map_res.set(s.id, s);
								}
							});
							data_filter = [...map_res.values()];
						}
						data_filter.sort((a, b) => {
							const a_date = moment(a.start_date, 'Do MMM YYYY').format('YYYYMMDD');
							const b_date = moment(b.start_date, 'Do MMM YYYY').format('YYYYMMDD');
							return Number.parseInt(a_date) - Number.parseInt(b_date);
						});

						if (this.completedJobPrivilege) {
							data_filter.forEach(d => {
								if (this.job_invalid_status_ids.includes(d.job_status.id)) {
									d['job_valid'] = false;
								} else {
									d['job_valid'] = true;
								}
							});						
						} else {
							data_filter.forEach(d => {
								d['job_valid'] = true;
							});
						}

						let search_obj = null;
						let sort_obj = null;

						if (Object.keys(selected_filter_params).length !== 0) {
							const search_obj_tmp = selected_filter_params['search'];
							if (search_obj_tmp) {
								search_obj = search_obj_tmp;
							} else {
								search_obj = null;
							}

							const sort_obj_tmp = selected_filter_params['sort'];
							if (sort_obj_tmp) {
								sort_obj = sort_obj_tmp;
							}
						}

						if (worklist_id === 0) {
							const tasklist_obj = {
								id: worklist_id,
								class: TASKLIST_CLASS,
								name: '',
								settings: settings,
								is_dynamic: true,
								switch: true,
								search_init: search_obj,
								sort_init: sort_obj,
								task: data_filter
							};
							const exsit_tasklist_arr = this.tasklist.filter(t => {return t.id !== worklist_id});
							this.tasklist = [...[tasklist_obj], ...exsit_tasklist_arr];
							
							if (isSearch && !this.current_unsaved_ids.includes(tasklist_obj.id)) {
								this.current_unsaved_ids.push(tasklist_obj.id);
							}
						} else {
							const worklist_item_arr = this.tasklist.filter(t => { return t.id === worklist_id });
							const item = worklist_item_arr[0];

							/** if worklist is not belongs to current user, save as */
							const is_owned = item.is_owned;

							if (isSearch && is_owned) {
								const param = {
									id: item.id,
									class: item.class,
									name: item.name,
									settings: typeof(settings) === 'string' ? settings :JSON.stringify(settings),
									is_dynamic: item.is_dynamic,
									search_init: search_obj,
									sort_init: sort_obj,
									details: item.details ? item.details: null,
								};
								this._updateTasklist(item.id, param, isSearch);
							}


							const tasklist_tmp = [];
							this.tasklist.forEach(t => {
								if (t.id === worklist_id) {
									t.task = data_filter;
									t.settings = settings;
									t.search_init = search_obj,
									t.sort_init = sort_obj

									tasklist_tmp.push(t);
								} else {
									tasklist_tmp.push(t);
								}
							});
							this.tasklist = tasklist_tmp;

							if (isSearch && !this.current_unsaved_ids.includes(worklist_id) && !is_owned) {
								this.current_unsaved_ids.push(worklist_id);
							}
						}

					} else {
						this.error_msg = reason;
					}
				})
				.catch((e) => {
					this.error_msg = e.message;
				}).finally(() => {
					if (this.loading) {
						this.loading = false;
					}
					if (this.loading_key !== -1) {
						this.loading_key = -1;
					}
				});
		},
		_loadTasklist(id) {
			const url = `/api/worklists/${id}`;

			axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then((val) => {
					let json = val.data;

					// For some reason axios isn't always converting our data
					if ((typeof json) == "string") {
						json = JSON.parse(json);
					}

					const {  status, data, reason } = json;

					if (status == "OK") {
						this.share_item = data;
					} else {
						this.error_msg = reason;
					}
				})
				.catch((e) => {
					this.error_msg = e.message;
				});
		},
		async _createTasklist(param) {
			const url = '/api/worklists';

			await axios
				.post(url, param, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then(val => {
					const { status, data, reason } = val.data;

					if (status == "OK") {
						if (typeof(this.save_as_item.id) === 'string' && this.save_as_item.id.startsWith('0')) {
							const tasklist_temp = this.tasklist.filter(t => { return t.id !== this.save_as_item.id });
							data['switch'] = false;
							data['task'] = [];
							tasklist_temp.unshift(data);
							this.tasklist = tasklist_temp;
						} else {
							data['switch'] = false;
							data['task'] = [];
							this.tasklist.unshift(data);
						}	

						if (this.current_unsaved_ids.length !== 0) {
							this.current_unsaved_ids = this.current_unsaved_ids.filter(c => { return c !== this.save_as_item.id });
						}

						this.notice_text.push(`Tasklist ‘${param.name}’ has been created.`);
						this.visible_notice = true;
						this.tid = setTimeout(this._close, 2000);

						this.save_as_visible = false;
						this.save_as_item = null;

						this.getTasklists();
					} else {
						thius.error_msg = reason;
					}
				})
				.catch((e) => {
					this.error_msg = e.message;
				});
		},
		async _updateTasklist(id, param, is_search=false) {
			const url = `/api/worklists/${id}`;
			await axios
				.put(url, param, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then(val => {
					const { status, data, reason } = val.data;

					if (status == "OK") {
						if (!is_search) {
							const tasklist_tmp = [];
							this.tasklist.forEach(t => {
								if (t.id === id) {
									tasklist_tmp.push(data);
								} else {
									tasklist_tmp.push(t);
								}
							});
							this.tasklist = tasklist_tmp;

							if (this.current_unsaved_ids.length !== 0) {
								this.current_unsaved_ids = this.current_unsaved_ids.filter(current_unsaved_id => { return current_unsaved_id !== id});
							}

							this.notice_text.push(`${data.name} has been changed successfully`);
							this.visible_notice = true;
							this.tid = setTimeout(this._close, 2000);
							
							this.edit_id = -1;
							if (data.is_pinned) {
								this.$store.dispatch('update_pinned_tasks_worklists');
							}
						}
					} else {
						this.error_msg = reason;
					}
				})
				.catch((e) => {
					this.error_msg = e.message;
				});
		},
		_pinTasklist(item, enabled) {
			const url = `/api/worklists/${item.id}/pinned`;

			const param = {enabled: enabled }

			axios
				.put(url, param, {
					"Authorization": "Bearer " + this.$store.state.token,
					"Accept": "application/json"				
				})
				.then(val =>  {
					const {status, reason} = val.data;

					if (status == "OK") {
						if (enabled) {
							this.notice_text.push(`Tasklist ‘${item.name}’ has been pinned.`);
						} else {
							this.notice_text.push(`Tasklist ‘${item.name}’ has been unpinned.`);
						}
						
						this.visible_notice = true;
						this.tid = setTimeout(this._close, 2000);

						this.$store.dispatch('update_pinned_tasks_worklists');

						const tasklist_tmp = [];
						this.tasklist.forEach(t => {
							if (t.id === item.id) {
								t['is_pinned'] = enabled;
								tasklist_tmp.push(t);
							} else {
								tasklist_tmp.push(t);
							}
						});

						this.tasklist = tasklist_tmp;
						
					} else {
						this.$emit('err-msg', reason);
					}
				})
				.catch((e) => {
					this.$emit('err-msg', e);
				});

		},
		_deleteTasklist(id, name) {
			const url = `/api/worklists/${id}`;

			axios
				.delete(url, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then(val => {
					const { status, reason} = val.data;

					if (status == "OK") {
						this.notice_text.push(`Tasklist ‘${name}’ has been successfully deleted.`);
						this.visible_notice = true;
						this.tid = setTimeout(this._close, 2000);

						const index = this.tasklist.findIndex(t => { return t.id === id });
						this.tasklist.splice(index, 1);
						this.delete_item = null;
						this.$store.dispatch('update_pinned_tasks_worklists');
						
					} else {
						this.error_msg = reason;
					}
				})
				.catch(e => {
					this.error_msg = e.message;
				});
		},
		_deleteTask(id, name, tasklist_id) {
			const url = `/api/tasks/${id}`;
			axios
				.delete(url, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then(val => {
					const { status, reason} = val.data;

					if (status == "OK") {
						this.notice_text.push(`Task ‘(${name})’ has been deleted successfully.`);
						this.visible_notice = true;
						this.tid = setTimeout(this._close, 2000);

						this.tasklist.forEach(w => {
							if (w.id === tasklist_id) {
								const tasks_tmp = w.task;
								const index = tasks_tmp.findIndex(d => { return d.id === id });
								if (index !== -1) {
									tasks_tmp.splice(index, 1);
								}
								w.task = tasks_tmp;
							}
						});

						this.delete_task = null;
					} else {
						this.error_msg = reason;
					}
				})
				.catch(e => {
					this.error_msg = e.message;
				});
		},
		_setParams(settings) {
			const settings_obj = settings && typeof(settings) === 'string' ? JSON.parse(settings) : settings;

			const params = [];

			/*  if no assign_mode, assign_mode=0, 0 = you */
			const assign_mode = settings_obj.assign_mode ? settings_obj.assign_mode : 0;
			params.push("assign_mode=" + assign_mode);

			/*  date_mode */
			const date_mode = settings_obj.date_mode;
			params.push("date_mode=" + date_mode);

			/*  if no budget_types, budget_types=[0, 1, 2, 3], 0=No units/value, 1=Value only, 2=Minutes + Value, 3=Units + Value */
			const budget_types = settings_obj.budget_types ? settings_obj.budget_types : [0, 1, 2, 3];
			params.push("budget_types=" + budget_types);

			if (settings_obj.statusses) {
				params.push("statusses=" + settings_obj.statusses);
			}
			if (settings_obj.job_types) {
				params.push("job_types=" + settings_obj.job_types);
			}
			if (settings_obj.priorities) {
				params.push("priorities=" + settings_obj.priorities);
			}
			if (settings_obj.date_mode && settings_obj.date_mode === 2) {
				if (settings_obj.start_date && settings_obj.start_date.length !== 0) {
					params.push("start_date=" + encodeURIComponent(settings_obj.start_date));
				}
				if (settings_obj.end_date && settings_obj.end_date.length !== 0) {
					params.push("end_date=" + encodeURIComponent(settings_obj.end_date));
				}
			}
			if (settings_obj.departments && settings_obj.departments.length !== 0) {
				params.push("departments=" + settings_obj.departments);
			}
			if (settings_obj.roles && settings_obj.roles.length !== 0) {
				params.push("roles=" + settings_obj.roles);
			}
			if (settings_obj.employees && settings_obj.employees.length !== 0) {
				params.push("employees=" + settings_obj.employees);
			}
			if (settings_obj.suggested_mode !== 'undefined' || settings_obj.suggested_mode !== null) {
				params.push("suggested_mode=" + settings_obj.suggested_mode);
			}
			
			return params
		},
		_setFilterParams(settings) {
			const filter_params = [];

			const settings_obj = settings && typeof(settings) === 'string' ? JSON.parse(settings) : settings;

			if (typeof(settings_obj.suggested_mode) === 'undefined' || settings_obj.suggested_mode === null) {
				filter_params.push({ date_type: [Date_Options_Suggested, Date_Options_Actual, Date_Options_No] });
			} else {
				filter_params.push({ date_type: settings_obj.date_type });
			}
			
			return filter_params;
		},
		_getSelectedFiltersParams(settings) {
			const settings_obj = settings && typeof(settings) === 'string' ? JSON.parse(settings) : settings;
			
			const filter_params = {};
			if (settings_obj.sort) {
				filter_params['sort'] = settings_obj.sort;
			}

			if (settings_obj.search) {
				filter_params['search'] = settings_obj.search;
			}

			return filter_params;
		},
		_filterDate(data, filter_params) {
			let data_filter = [];

			filter_params.forEach(obj => {
				for (const [key, value] of Object.entries(obj)) {
					if (key === 'date_type') {
						if (value.length === 1) {
							if (value[0] === Date_Options_Suggested) {
								data_filter = data.filter(d => { return d.is_suggested === true });
							}
							if (value[0] === Date_Options_Actual) {
								const data_actual = data.filter(d => { return d.is_suggested === false });
								data_filter = data_actual.filter(d => { return d.date !== null });
							} 
							if (value[0] === Date_Options_No) {
								data_filter = data.filter(d => { return d.date === null });
							} 
						}
						

						if (value.length === 2) {
							if (value.includes(Date_Options_No) && value.includes(Date_Options_Suggested)) {
								const data_suggested = data.filter(d => { return d.is_suggested === true });
								const data_no_date = data.filter(d => { return d.date === null });
								data_filter = [...data_suggested, ... data_no_date];
							}
							if (value.includes(Date_Options_No) && value.includes(Date_Options_Actual)) {
								const data_actual = data.filter(d => { return d.is_suggested === false });
								data_filter = data_actual;
							}
							if (value.includes(Date_Options_Suggested) && value.includes(Date_Options_Actual)) {
								const data_suggested = data.filter(d => { return d.is_suggested === true });
								const data_actual = data.filter(d => { return d.is_suggested === false });
								const data_filter_tmp = [...data_suggested, ...data_actual];
								data_filter = data_filter_tmp.filter(d => { return d.date !== null });
							}
						}

						if (value.length === 3) {
							data_filter = data;
						}
					}
				}
			});

			return data_filter;
		},
		_close() {
			this.visible_notice = false;
			this.notice_text = [];
		},
		_minutesToBudget(value) {
			let result = 0;

			if (value < 60 && value >= 0) {
				result = `${value}m`;
			}

			if (value >= 60 && value < 24 * 60) {
				const hours = Math.floor(value / 60);
				const minutes = value - (hours * 60);
				result = minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
			}

			if (value >= 24 * 60) {
				const days = Math.floor(value / (24 * 60));

				const hour_tmp = value - (days * 24 * 60);
				const hours = Math.floor(hour_tmp / 60);

				const minutes = hour_tmp - (hours * 60);
				
				const part_result = hours > 0 ? `${days}d ${hours}h` : `${days}d`;
				result = minutes > 0 ? `${part_result} ${minutes}m` : `${part_result}`;
			}

			return result;
		},	
	}
}
</script>
<style lang="scss" scoped>
.task-list-item {
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
}
.task-title {
	width: 100%;
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
}
.save-as {
	display: flex;
	flex-flow: row nowrap;
	justify-content: space-between;
	align-items: center;
	font-family: Roboto;
	font-style: normal;
	font-weight: 500;
	font-size: 16px;
	line-height: 20px;
	// color: #00AAFD;
}
.save-as-bnt {
	background: #F45252;
	border-radius: 4px;
	color: #FFFFFF;
	font-family: Roboto;
	font-style: normal;
	font-weight: 500;
	font-size: 14px;
	text-align: center;
	margin-left: 20px;
	padding: 6px 12px;
	line-height: 20px;
	&:hover {
		cursor: pointer;
	}
}
.delete-bnt {
	border: 1px solid #F45252;
	border-radius: 4px;
	color: #F45252;
	font-family: Roboto;
	font-style: normal;
	font-weight: 500;
	font-size: 14px;
	text-align: center;
	margin-left: 20px;
	padding: 6px 12px;
	line-height: 20px;
	&:hover {
		cursor: pointer;
	}
}
</style>